// apolo/src/lib/loop/checkpointWire.js — Fix 3b (code-tab-to-10).
// @arch:pure-engine
//
// Factory that builds the loop-scoped checkpointer from injected deps.
// Extracted from the spine to keep the DP/LOC ratchet flat.
// PURE: no react/window — the shell port is injected by the caller.

import { createLoopCheckpointer } from './checkpointGate.js';
import { makeGitCheckpoint } from '../../checkpoint/gitCheckpoint.js';
import { readCheckpointLoopEnabled } from '../../flags/sprintFlags.js';

/**
 * Build a loop-scoped checkpointer from injected dependencies.
 * When the flag is OFF or no runCommand is provided, returns a no-op pair.
 *
 * @param {string} root — worktree root
 * @param {Function|null} [runCommand] — ({command, cwd}) → shell result
 * @returns {{ onGreen: Function, onFail: Function, store: object|null }}
 */
export function buildLoopCheckpointer(root, runCommand) {
  const enabled = readCheckpointLoopEnabled();
  const gitCp = enabled && runCommand ? makeGitCheckpoint({ root, runCommand }) : null;
  // Simple in-memory checkpoint store (the loop is per-session; a fresh store
  // per loop run is correct — checkpoints don't need to survive a reload).
  const createCheckpointStore = () => ({ checkpoints: [] });
  return createLoopCheckpointer({
    enabled,
    root,
    snapshot: gitCp ? (async () => gitCp.save()) : null,
    restore: gitCp ? (async ({ ref }) => gitCp.restore(ref)) : null,
    createCheckpointStore,
    isHonestGreen: (verdict) => verdict && (verdict.green === true || verdict.passed === true),
    captureCheckpoint: async (verdict, ctx, deps) => {
      try {
        const ref = deps.snapshot ? await deps.snapshot({ root: ctx.root, signal: ctx.signal }) : null;
        const checkpoint = { ref, taskId: ctx.taskId, at: new Date().toISOString() };
        if (deps.persist) try { deps.persist(checkpoint); } catch { /* best-effort */ }
        return { ok: true, checkpoint };
      } catch (e) {
        return { ok: false, error: String(e?.message || e) };
      }
    },
    restoreLatestGreen: async (store, opts, deps) => {
      if (!store?.checkpoints?.length) return { ok: false, reason: 'no checkpoints' };
      const latest = store.checkpoints[store.checkpoints.length - 1];
      if (!latest?.ref) return { ok: false, reason: 'no ref' };
      try {
        if (deps.restore) await deps.restore({ ref: latest.ref, root: opts.root });
        return { ok: true, ref: latest.ref };
      } catch (e) {
        return { ok: false, error: String(e?.message || e) };
      }
    },
  });
}
